import React, { useState, useContext } from 'react';
import { IconButton, ToggleButton, ToggleButtonGroup, Typography, Stack, Button } from '@mui/material';
import PhysiciansTable from './Physicians/PhysiciansTable';
import CardManagersTable from './CardManagers/CardManagersTable';
import { CloseRounded } from '@mui/icons-material';
import { useLogUserActionMutation } from '../../../api/apiSlice';
import BulkTextingDialog from '../../SuperSettings/PhysicianTexting/BulkTextingDialog';
import { UserContext } from '../../Hooks/useUser';

function MulticampaignDetails(props) {
    const [isShowingTexting, setIsShowingTexting] = useState(false);
    const { isSuperUser } = useContext(UserContext);

    // Merge/sum stats for each physician by ID
    const physicianMap = {};
    props.campaigns.flatMap(campaign => campaign.physicians || []).forEach(physician => {
        const id = physician.id;
        if (!id) return;
        if (!physicianMap[id]) {
            physicianMap[id] = { ...physician };
            // Create a new recommendation_summary object for summing
            if (physician.recommendation_summary) {
                physicianMap[id].recommendation_summary = { ...physician.recommendation_summary };
            }
        } else if (physician.recommendation_summary && physicianMap[id].recommendation_summary) {
            Object.keys(physician.recommendation_summary).forEach(recKey => {
                const value = physician.recommendation_summary[recKey];
                physicianMap[id].recommendation_summary[recKey] = (physicianMap[id].recommendation_summary[recKey] || 0) + value;
            });
        }
    });
    const physicians = Object.values(physicianMap);

    // Merge card managers across campaigns
    const cardManagerMap = {};
    props.campaigns.flatMap(campaign => campaign.card_managers || []).forEach(cardManager => {
        const id = cardManager.id;
        if (!id) return;
        const fields = [ 'num_open_reviews', 'num_accepted_reviews', 'num_rejected_reviews', 'num_overdue_reviews', 'open_review_value', 'accepted_review_value', 'rejected_review_value', 'overdue_review_value' ];
        if (!cardManagerMap[id]) {
            cardManagerMap[id] = { ...cardManager };
            // Ensure all summed fields are initialized to 0 if missing
            fields.forEach(field => {
                if (typeof cardManagerMap[id][field] !== 'number') {
                    cardManagerMap[id][field] = 0;
                }
            });
        } else {
            // Sum the following fields (all are guaranteed numbers)
            fields.forEach(field => {
                const value = cardManager[field] || 0;
                cardManagerMap[id][field] = cardManagerMap[id][field] + value;
            });
        }
    });
    const cardManagers = Object.values(cardManagerMap);

    // Sum the card manager summary across campaigns (this has no duplication issues, so simpler than above)
    const cardManagerSummary = props.campaigns.reduce((summary, campaign) => {
        Object.entries(campaign.card_manager_summary || {}).forEach(([key, value]) => {
            summary[key] = (summary[key] || 0) + value;
        });
        return summary;
    }, {});

    const [logUserAction] = useLogUserActionMutation();
    const [usersToShow, setUsersToShow] = useState('physicians');
    const [numericFormat, setNumericFormat] = useState('currency');

    function handleToggleUsers(userType) {
        if (userType !== null) {
            setUsersToShow(userType);
            logUserAction({ action: 'toggled_campaign_monitoring_users', data: { 
                user_type: userType,
                campaigns: props.campaigns,
            } });
        }
    }

    function handleToggleNumericFormat(format) {
        if (format !== null) {
            setNumericFormat(format);
            logUserAction({ action: 'toggled_campaign_monitoring_numeric_format', data: { 
                numeric_format: format,
                campaigns: props.campaigns,
            } });
        }
    }

    return (
        <Stack sx={{ height: '100%' }}>
            <Stack direction='row' alignItems='center' spacing={1}>
                <Typography variant='heavy' fontSize='18px'><strong>Campaign Details: </strong>{props.campaigns.length} selected campaign{props.campaigns.length > 1 ? 's' : ''} </Typography>
                <IconButton onClick={() => props.setSelectedCampaigns(new Set())}><CloseRounded /></IconButton>
            </Stack>
            <Stack spacing={1} maxHeight='100%' pb={4}>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <ToggleButtonGroup size='small' value={usersToShow} exclusive onChange={(e, value) => handleToggleUsers(value)} color='primary'>
                        <ToggleButton value='physicians'>Physicians</ToggleButton>
                        <ToggleButton value='card_managers'>Card Managers</ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        { isSuperUser() && 
                            <Button variant='contained' color='primary' onClick={() => setIsShowingTexting(true)}>Send Bulk Text</Button>
                        }
                        <ToggleButtonGroup size='small' value={numericFormat} exclusive onChange={(e, value) => handleToggleNumericFormat(value)} color='primary'>
                            <ToggleButton value='currency'>$</ToggleButton>
                            <ToggleButton value='count'>#</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Stack>
                { usersToShow === 'physicians' &&
                    <PhysiciansTable physicians={physicians} appliedFilters={{}} numericFormat={numericFormat} />
                }
                { usersToShow === 'card_managers' &&
                    <CardManagersTable cardManagers={cardManagers} summaryStats={cardManagerSummary} appliedFilters={{}} numericFormat={numericFormat} />
                }
                { isShowingTexting &&
                    <BulkTextingDialog 
                        open={isShowingTexting}
                        onClose={() => setIsShowingTexting(false)}
                        physicianIds={physicians.map(p => p.id)}
                    />
                }
            </Stack>
        </Stack>
    );
}

export default MulticampaignDetails;